import {
  ReactElement,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box, Button, IconButton } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import { TOUCH_QUERY, useViewport } from "../../../utils/useViewport";

// Adds https:// to a website typed without it.
export const cleanUrl = (url?: string | null) => {
  const value = (url || "").trim();
  if (!value) return "";
  return value.includes("http://") || value.includes("https://")
    ? value
    : "https://" + value;
};

// A US number as the directories write them: "(248) 555-0100",
// "248.555.0100 ext. 12", "248 555 0100 x12", "+1 248-555-0100".
const US_PHONE =
  /^\s*(?:\+?1[\s.\-‐-―]*)?\(?(\d{3})\)?[\s.\-‐-―]*(\d{3})[\s.\-‐-―]*(\d{4})\s*(?:(?:ext\.?|extension|x)\s*(\d{1,6}))?\s*$/i;

// A tel: link, or undefined when the value is not one phone number.
export const phoneHref = (value?: string | null) => {
  const match = value ? US_PHONE.exec(value) : null;
  if (!match) return undefined;
  const [, area, exchange, line, extension] = match;
  // The comma makes the dialler pause, then dial the extension.
  return `tel:+1${area}${exchange}${line}${extension ? `,${extension}` : ""}`;
};

// A mailto: link, or undefined when the value is not an address ("TBD").
export const mailHref = (value?: string | null) => {
  const email = (value || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ? `mailto:${email}`
    : undefined;
};

// From 1200 px the cards keep their desktop layout: a column with the
// 240 px image and the action buttons beside the details.
export const useWideCards = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up("lg"), { noSsr: true });
};

export const TagButton = ({
  tag,
  onClick,
}: {
  tag: string;
  onClick: () => void;
}) => (
  <Button
    onClick={onClick}
    style={{
      marginRight: "5px",
      textTransform: "none",
      marginBottom: "5px",
    }}
    variant="contained"
    sx={{
      color: "black",
      backgroundColor: "#ffb30080",
      "&:hover": {
        backgroundColor: "#bd5187",
        color: "white",
      },
      [`@media ${TOUCH_QUERY}`]: { minHeight: 40 },
    }}
  >
    {tag}
  </Button>
);

export interface DirectoryCardAction {
  key: string;
  label: string;
  onClick?: () => void;
  // Opens in a new tab.
  href?: string;
}

export interface DirectoryContact {
  key: string;
  icon: ReactElement;
  text?: string;
  // Makes the row a tap link; plain text without it.
  href?: string;
  newTab?: boolean;
}

interface CompactDirectoryCardProps {
  imageSrc: string;
  title: string;
  onTitleClick: () => void;
  // Admin only.
  onEdit?: () => void;
  onNotes?: () => void;
  // Falsy entries (and contacts without text) are skipped, so optional ones
  // can be written inline.
  actions: Array<DirectoryCardAction | false | null | undefined>;
  contacts: Array<DirectoryContact | false | null | undefined>;
  description: ReactNode;
  tags?: string[];
  onTagClick: (tag: string) => void;
  selected?: boolean;
}

// Phones show this many lines of a description, then a More/Less toggle.
const PHONE_DESCRIPTION_LINES = 6;

const ACTION_CLASS =
  "bg-yellow hover:bg-magenta text-white font-bold py-2 px-4 rounded-xl hover:cursor-pointer inline-flex items-center justify-center";

// A Providers / Resources card below 1200 px: the image beside the title,
// the actions in a row that wraps, the contacts as links (two columns from
// 600 px, one on phones) and on phones a clamped description.
export const CompactDirectoryCard = ({
  imageSrc,
  title,
  onTitleClick,
  onEdit,
  onNotes,
  actions,
  contacts,
  description,
  tags,
  onTagClick,
  selected,
}: CompactDirectoryCardProps) => {
  const { isPhone } = useViewport();
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  // The toggle shows only when the clamp hides lines. Measured while
  // collapsed, so expanding keeps the "Less" button.
  useLayoutEffect(() => {
    if (expanded) return;
    const el = descriptionRef.current;
    setClamped(Boolean(isPhone && el && el.scrollHeight > el.clientHeight + 1));
  }, [isPhone, expanded]);

  const shownActions = actions.filter(
    (action): action is DirectoryCardAction => Boolean(action),
  );
  const shownContacts = contacts.filter(
    (contact): contact is DirectoryContact => Boolean(contact && contact.text),
  );

  return (
    <Box
      className={`bg-white rounded-lg shadow-md ${
        selected ? "border-2 border-magenta" : ""
      }`}
      sx={{ p: { xs: 1.5, sm: 2 }, textAlign: "left" }}
    >
      {/* Phones: the avatar beside the title, the actions below both. From
          600 px the actions sit beside the image, under the title. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          gridTemplateRows: { xs: "auto auto", sm: "auto 1fr" },
          gridTemplateAreas: {
            xs: '"image title" "actions actions"',
            sm: '"image title" "image actions"',
          },
          alignItems: "start",
          columnGap: 1.5,
          rowGap: 1.5,
        }}
      >
        <Box
          className="bg-white rounded-lg flex items-center justify-center"
          sx={{
            gridArea: "image",
            width: { xs: 56, sm: 96 },
            height: { xs: 56, sm: 96 },
          }}
        >
          <img src={imageSrc} className="object-contain w-full h-full" alt="" />
        </Box>
        <Box
          sx={{
            gridArea: "title",
            alignSelf: { xs: "center", sm: "start" },
            display: "flex",
            alignItems: "flex-start",
          }}
        >
          <h3
            className="text-lg font-bold thrive-user-text"
            style={{ flex: 1, minWidth: 0 }}
          >
            <button
              type="button"
              className="text-left font-bold"
              onClick={onTitleClick}
            >
              {title}
            </button>
          </h3>
          {onEdit && (
            <IconButton aria-label={`Edit ${title}`} onClick={onEdit}>
              <EditIcon className="text-magenta" />
            </IconButton>
          )}
          {onNotes && (
            <IconButton aria-label={`Notes for ${title}`} onClick={onNotes}>
              <DescriptionIcon className="text-magenta" />
            </IconButton>
          )}
        </Box>
        {shownActions.length > 0 && (
          <Box
            sx={{
              gridArea: "actions",
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              "& > *": { flexGrow: { xs: 1, sm: 0 } },
            }}
          >
            {shownActions.map((action) =>
              action.href ? (
                <a
                  key={action.key}
                  className={ACTION_CLASS}
                  href={action.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {action.label}
                </a>
              ) : (
                <button
                  key={action.key}
                  type="button"
                  className={ACTION_CLASS}
                  onClick={action.onClick}
                >
                  {action.label}
                </button>
              ),
            )}
          </Box>
        )}
      </Box>

      {shownContacts.length > 0 && (
        <Box
          component="ul"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            columnGap: 2,
            mt: 1,
          }}
        >
          {shownContacts.map(({ key, icon, text, href, newTab }) => {
            const content = (
              <>
                {icon}
                <span className="min-w-0">{text}</span>
              </>
            );
            return (
              <li key={key} className="thrive-user-text">
                {href ? (
                  <a
                    className="flex items-center gap-2 min-h-[40px] text-magenta"
                    href={href}
                    target={newTab ? "_blank" : undefined}
                    rel={newTab ? "noreferrer" : undefined}
                  >
                    {content}
                  </a>
                ) : (
                  <span className="flex items-center gap-2 min-h-[40px]">
                    {content}
                  </span>
                )}
              </li>
            );
          })}
        </Box>
      )}

      <Box
        ref={descriptionRef}
        className="text-sm thrive-user-text"
        sx={{
          mt: 1,
          "& ul": { pl: 2.5 },
          ...(isPhone &&
            !expanded && {
              display: "-webkit-box",
              WebkitLineClamp: PHONE_DESCRIPTION_LINES,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }),
        }}
      >
        {description}
      </Box>
      {clamped && (
        <Button
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          sx={{ minHeight: 40, color: "#b3197a" }}
        >
          {expanded ? "Less" : "More"}
        </Button>
      )}

      {Array.isArray(tags) && tags.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", mt: 1.5 }}>
          {tags.map((tag, index) => (
            <TagButton key={index} tag={tag} onClick={() => onTagClick(tag)} />
          ))}
        </Box>
      )}
    </Box>
  );
};
